import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FeatureFlagPublic } from '@mondy/shared';
import { api } from '@/api';
import { AdminNav } from '@/components/admin/AdminNav';

interface FlagsResponse {
  flags: FeatureFlagPublic[];
}

const FLAG_LABELS: Record<string, { label: string; description: string }> = {
  predictions_revealed_at_kickoff: {
    label: 'Hide predictions until kickoff',
    description:
      'When enabled, other players\u2019 predictions are hidden until the match kicks off. Disable to reveal all predictions immediately (useful for testing).',
  },
};

export function AdminSettingsPage() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['admin', 'flags'],
    queryFn: () => api.get<FlagsResponse>('/api/admin/flags'),
  });

  const toggle = useMutation({
    mutationFn: (args: { key: string; enabled: boolean }) =>
      api.put(`/api/admin/flags/${args.key}`, { enabled: args.enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'flags'] }),
  });

  const flags = query.data?.flags ?? [];

  const allKeys = Object.keys(FLAG_LABELS);
  const flagsByKey = new Map(flags.map((f) => [f.key, f]));

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-5 p-4 md:p-8">
      <AdminNav />
      <h1 className="text-lg font-bold">Settings</h1>

      <div className="flex flex-col gap-3">
        {allKeys.map((key) => {
          const meta = FLAG_LABELS[key]!;
          const current = flagsByKey.get(key);
          const enabled = current?.enabled ?? true;

          return (
            <div
              key={key}
              className="flex items-start justify-between gap-4 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4"
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold">{meta.label}</p>
                <p className="mt-1 text-xs text-[color:var(--color-fg-muted)]">
                  {meta.description}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={enabled}
                disabled={toggle.isPending}
                onClick={() => toggle.mutate({ key, enabled: !enabled })}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors ${
                  enabled
                    ? 'bg-[color:var(--color-accent)]'
                    : 'bg-[color:var(--color-border)]'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                    enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
